import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { RangeValidator } from '~services/custom-validators';
import { CrudService } from '~services/crud.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BonusApiService } from '~services/api/bonus.api.service';
import { Router } from '@angular/router';
import { trackById } from '~lmo/utilities';
import { FracIdAndJobName } from '~models/frac.model';
import { filter, map, startWith, take } from 'rxjs/operators';
import { BonusContract, BonusMines, BonusRegion } from '~models/bonus.model';

@Component({
  selector: 'sa-bonus-event-detail',
  templateUrl: './bonus-event-detail.component.html',
  styleUrls: ['./bonus-event-detail.component.scss'],
})
export class BonusEventDetailComponent implements OnInit {
  @Input() selectedBonusEvent: any;
  @Input() type: string;

  public types = [
    { name: 'Percent', value: 'percent' },
    { name: 'Flat Rate Per Load', value: 'flat_rate_cents' },
    { name: 'Per Ton', value: 'per_ton' },
    { name: 'Per Approved Mile', value: 'per_approved_mile' },
  ];
  public isFlatRateSelected$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isPercentSelected$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isPerTonSelected$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isPerApprovedMileSelected$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public selectedFracs: string[] = [];
  public selectedRegions: string[] = [];
  public selectedRegionsNumber: number[] = [];
  public selectedFracsNumber: number[] = [];
  public selectedMinesNumber: number[] = [];
  public selectedContractsNumber: number[] = [];
  public isDisableForm = false;

  constructor(
    private fb: FormBuilder,
    private crud: CrudService,
    private snackBar: MatSnackBar,
    private bonusApiService: BonusApiService,
    public router: Router,
  ) {}

  bonusEventForm: FormGroup;
  fracs: FracIdAndJobName[];
  filteredFracs$: Observable<FracIdAndJobName[]>;
  filterFrac: FormControl = new FormControl();
  regions: BonusRegion[];
  filteredRegions$: Observable<BonusRegion[]>;
  filterRegion: FormControl = new FormControl();
  contracts: BonusContract[];
  filteredContracts$: Observable<BonusContract[]>;
  filterContract: FormControl = new FormControl();
  mines: BonusMines[];
  filteredMines$: Observable<BonusMines[]>;
  filterMine: FormControl = new FormControl();

  compareFn: ((o1: any, o2: any) => boolean) | null = this.compareByValue;
  public trackById = trackById;

  ngOnInit() {
    this.crud.httpClientReady.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.getFracIdsAndJobNames();
    });
    this.buildForm();
    this.listenToValueChanges();

    if (this.type === 'view') {
      this.isDisableForm = true;
    }

    if (!this.selectedBonusEvent) {
      this.applyScopeChrome('fracs');
      this.getRegionsForBonus();
    }

    if (this.selectedBonusEvent) {
      if (!this.isDisableForm) {
        this.isDisableForm = this.selectedBonusEvent.archivedAt !== null;
      }

      const scopeMode: 'fracs' | 'regions' = this.selectedBonusEvent.regions?.length > 0 ? 'regions' : 'fracs';

      this.bonusEventForm.patchValue(
        {
          name: this.selectedBonusEvent.name,
          startAt: this.selectedBonusEvent.startAt,
          endAt: this.selectedBonusEvent.endAt,
          type: this.selectedBonusEvent.pricingType,
          quota: this.selectedBonusEvent.quota,
          percentValue: this.selectedBonusEvent.pricingValue,
          scopeMode,
          contract: this.selectedBonusEvent.contracts || [],
        },
        { emitEvent: false },
      );

      this.applyScopeChrome(scopeMode);

      if (scopeMode === 'fracs') {
        this.bonusEventForm.patchValue({
          frac: this.selectedBonusEvent.fracs || [],
          mine: this.selectedBonusEvent.mines || [],
          region: [],
        });
      } else {
        this.bonusEventForm.patchValue({
          region: this.selectedBonusEvent.regions || [],
          frac: [],
          mine: [],
        });
      }

      this.selectedFracs = this.constructIdArray(this.bonusEventForm.get('frac')?.value);
      this.selectedFracsNumber = this.selectedFracs.map((str) => parseInt(str, 10));
      this.selectedRegions = this.constructIdArray(this.bonusEventForm.get('region')?.value);
      this.selectedRegionsNumber = this.selectedRegions.map((str) => parseInt(str, 10));
      this.selectedMinesNumber = this.constructIdArray(this.bonusEventForm.get('mine')?.value).map((str) =>
        parseInt(str, 10),
      );
      this.selectedContractsNumber = this.constructIdArray(this.bonusEventForm.get('contract')?.value).map((str) =>
        parseInt(str, 10),
      );

      if (this.selectedBonusEvent.pricingType === 'percent') {
        this.bonusEventForm.get('percentValue').setValue(this.selectedBonusEvent.pricingValue);
        if (!this.selectedBonusEvent.priceCap) {
          this.bonusEventForm.get('priceCap').setValue('');
        } else {
          this.bonusEventForm.get('priceCap').setValue((this.selectedBonusEvent.priceCap / 100).toFixed(2));
        }
      } else if (this.selectedBonusEvent.pricingType === 'per_ton') {
        this.bonusEventForm.get('perTonValue').setValue(this.selectedBonusEvent.pricingValue);
        if (!this.selectedBonusEvent.priceCap) {
          this.bonusEventForm.get('priceCap').setValue('');
        } else {
          this.bonusEventForm.get('priceCap').setValue((this.selectedBonusEvent.priceCap / 100).toFixed(2));
        }
      } else if (this.selectedBonusEvent.pricingType === 'per_approved_mile') {
        this.patchPerApprovedMileFromEvent(this.selectedBonusEvent);
        if (!this.selectedBonusEvent.priceCap) {
          this.bonusEventForm.get('priceCap').setValue('');
        } else {
          this.bonusEventForm.get('priceCap').setValue((this.selectedBonusEvent.priceCap / 100).toFixed(2));
        }
      } else {
        this.bonusEventForm.get('priceValue').setValue(this.selectedBonusEvent.pricingValue);
      }

      this.syncPricingTypeSubjects(this.selectedBonusEvent.pricingType);
      this.applyPricingTypeValidators(this.selectedBonusEvent.pricingType);
      this.checkDisablingContract(this.selectedRegions, this.selectedFracs);
      this.getBonusContracts();
      if (scopeMode === 'fracs') {
        this.getBonusMines();
      }
      this.getRegionsForBonus();

      if (this.isDisableForm) {
        this.bonusEventForm.disable();
      }
    }
  }

  private patchPerApprovedMileFromEvent(event: any) {
    const cents = event.perApprovedMileValue;
    if (cents != null && cents !== '') {
      this.bonusEventForm.get('perApprovedMileValue').setValue(Number(cents) / 100);
      return;
    }
    const pv = event.pricingValue;
    if (typeof pv === 'number' && !Number.isNaN(pv)) {
      this.bonusEventForm.get('perApprovedMileValue').setValue(pv);
      return;
    }
    if (typeof pv === 'string') {
      const match = pv.match(/\$?\s*([\d.]+)/);
      if (match) {
        this.bonusEventForm.get('perApprovedMileValue').setValue(parseFloat(match[1]));
      }
    }
  }

  private applyScopeChrome(mode: 'fracs' | 'regions') {
    const fracCtrl = this.bonusEventForm.get('frac');
    const mineCtrl = this.bonusEventForm.get('mine');
    const regionCtrl = this.bonusEventForm.get('region');
    if (mode === 'fracs') {
      regionCtrl?.disable({ emitEvent: false });
      fracCtrl?.enable({ emitEvent: false });
      mineCtrl?.enable({ emitEvent: false });
    } else {
      regionCtrl?.enable({ emitEvent: false });
      fracCtrl?.disable({ emitEvent: false });
      mineCtrl?.disable({ emitEvent: false });
    }
  }

  private applyPricingTypeValidators(selectedType: string) {
    if (selectedType === 'flat_rate_cents') {
      this.bonusEventForm
        .get('priceValue')
        .setValidators([Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]);
      this.bonusEventForm.get('percentValue').clearValidators();
      this.bonusEventForm.get('perTonValue').clearValidators();
      this.bonusEventForm.get('perApprovedMileValue').clearValidators();
    } else if (selectedType === 'percent') {
      this.bonusEventForm
        .get('percentValue')
        .setValidators([Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/), RangeValidator(0, 30)]);
      this.bonusEventForm.get('priceValue').clearValidators();
      this.bonusEventForm.get('perTonValue').clearValidators();
      this.bonusEventForm.get('perApprovedMileValue').clearValidators();
    } else if (selectedType === 'per_ton') {
      this.bonusEventForm.get('perTonValue').setValidators([Validators.required]);
      this.bonusEventForm.get('priceValue').clearValidators();
      this.bonusEventForm.get('percentValue').clearValidators();
      this.bonusEventForm.get('perApprovedMileValue').clearValidators();
    } else if (selectedType === 'per_approved_mile') {
      this.bonusEventForm
        .get('perApprovedMileValue')
        .setValidators([Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]);
      this.bonusEventForm.get('priceValue').clearValidators();
      this.bonusEventForm.get('percentValue').clearValidators();
      this.bonusEventForm.get('perTonValue').clearValidators();
    } else {
      this.bonusEventForm.get('priceValue').clearValidators();
      this.bonusEventForm.get('percentValue').clearValidators();
      this.bonusEventForm.get('perTonValue').clearValidators();
      this.bonusEventForm.get('perApprovedMileValue').clearValidators();
    }
    this.bonusEventForm.get('priceValue').updateValueAndValidity();
    this.bonusEventForm.get('percentValue').updateValueAndValidity();
    this.bonusEventForm.get('perTonValue').updateValueAndValidity();
    this.bonusEventForm.get('perApprovedMileValue').updateValueAndValidity();
  }

  private syncPricingTypeSubjects(selectedType: string) {
    if (selectedType === 'flat_rate_cents') {
      this.isFlatRateSelected$$.next(true);
      this.isPercentSelected$$.next(false);
      this.isPerTonSelected$$.next(false);
      this.isPerApprovedMileSelected$$.next(false);
    } else if (selectedType === 'percent') {
      this.isFlatRateSelected$$.next(false);
      this.isPercentSelected$$.next(true);
      this.isPerTonSelected$$.next(false);
      this.isPerApprovedMileSelected$$.next(false);
    } else if (selectedType === 'per_ton') {
      this.isFlatRateSelected$$.next(false);
      this.isPercentSelected$$.next(false);
      this.isPerTonSelected$$.next(true);
      this.isPerApprovedMileSelected$$.next(false);
    } else if (selectedType === 'per_approved_mile') {
      this.isFlatRateSelected$$.next(false);
      this.isPercentSelected$$.next(false);
      this.isPerTonSelected$$.next(false);
      this.isPerApprovedMileSelected$$.next(true);
    } else {
      this.isFlatRateSelected$$.next(false);
      this.isPercentSelected$$.next(false);
      this.isPerTonSelected$$.next(false);
      this.isPerApprovedMileSelected$$.next(false);
    }
  }

  public buildForm() {
    this.bonusEventForm = this.fb.group({
      name: ['', [Validators.required]],
      startAt: ['', [Validators.required]],
      endAt: ['', [Validators.required]],
      quota: [''],
      type: ['', [Validators.required]],
      scopeMode: ['fracs' as 'fracs' | 'regions', [Validators.required]],
      priceValue: [{ value: '' }, [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      percentValue: [
        { value: '' },
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/), RangeValidator(0, 30)],
      ],
      perTonValue: ['', [Validators.required]],
      perApprovedMileValue: [{ value: '' }, [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      priceCap: [''],
      frac: [{ value: '', disabled: false }],
      mine: [{ value: '', disabled: false }],
      region: [{ value: '', disabled: false }],
      contract: [{ value: '', disabled: true }],
    });
  }

  public onSubmit() {
    if (this.bonusEventForm.valid) {
      const body: Record<string, unknown> = {
        name: this.bonusEventForm.controls['name'].value,
        startAt: this.bonusEventForm.controls['startAt'].value,
        endAt: this.bonusEventForm.controls['endAt'].value,
        pricingType: this.bonusEventForm.controls['type'].value,
      };

      const scopeMode = this.bonusEventForm.controls['scopeMode'].value as 'fracs' | 'regions';
      if (scopeMode === 'fracs') {
        body['fracIDs'] = this.selectedFracsNumber;
        body['regionIDs'] = [];
      } else {
        body['fracIDs'] = [];
        body['regionIDs'] = this.selectedRegionsNumber;
      }

      if (scopeMode === 'fracs' && this.selectedMinesNumber.length > 0) {
        body['mineIDs'] = this.selectedMinesNumber;
      }

      if (this.bonusEventForm.controls['contract'].value) {
        body['contractIDs'] = this.selectedContractsNumber;
      }

      if (this.bonusEventForm.controls['quota'].value) {
        body['quota'] = this.bonusEventForm.controls['quota'].value;
      }

      const pricingType = this.bonusEventForm.controls['type'].value;
      if (pricingType === 'flat_rate_cents') {
        this.bonusEventForm.controls['percentValue'].setValue('');
        this.bonusEventForm.controls['perTonValue'].setValue('');
        this.bonusEventForm.controls['perApprovedMileValue'].setValue('');
        this.bonusEventForm.controls['priceCap'].setValue('');

        const priceValue = this.bonusEventForm.controls['priceValue'].value * 100;
        body['priceValue'] = priceValue;
      } else if (pricingType === 'per_ton') {
        this.bonusEventForm.controls['percentValue'].setValue('');
        this.bonusEventForm.controls['priceValue'].setValue('');
        this.bonusEventForm.controls['perApprovedMileValue'].setValue('');

        const perTonValue = this.bonusEventForm.controls['perTonValue'].value * 100;
        body['perTonValue'] = perTonValue;

        if (this.bonusEventForm.controls['priceCap'].value) {
          const priceCap = (this.bonusEventForm.controls['priceCap'].value * 100).toFixed(0);
          body['priceCap'] = parseFloat(priceCap);
        }
      } else if (pricingType === 'per_approved_mile') {
        this.bonusEventForm.controls['percentValue'].setValue('');
        this.bonusEventForm.controls['priceValue'].setValue('');
        this.bonusEventForm.controls['perTonValue'].setValue('');

        const perApprovedMileValue = Math.round(
          Number(this.bonusEventForm.controls['perApprovedMileValue'].value) * 100,
        );
        body['perApprovedMileValue'] = perApprovedMileValue;

        if (this.bonusEventForm.controls['priceCap'].value) {
          const priceCap = (this.bonusEventForm.controls['priceCap'].value * 100).toFixed(0);
          body['priceCap'] = parseFloat(priceCap);
        }
      } else {
        this.bonusEventForm.controls['priceValue'].setValue('');
        this.bonusEventForm.controls['perTonValue'].setValue('');
        this.bonusEventForm.controls['perApprovedMileValue'].setValue('');

        body['percentValue'] = this.bonusEventForm.controls['percentValue'].value / 100;

        if (this.bonusEventForm.controls['priceCap'].value) {
          const priceCap = (this.bonusEventForm.controls['priceCap'].value * 100).toFixed(0);
          body['priceCap'] = parseFloat(priceCap);
        }
      }

      return body;
    }

    return null;
  }

  listenToValueChanges() {
    this.bonusEventForm.get('scopeMode')?.valueChanges.subscribe((mode: 'fracs' | 'regions') => {
      if (mode === 'fracs') {
        this.bonusEventForm.get('region')?.setValue([]);
        this.selectedRegions = [];
        this.selectedRegionsNumber = [];
      } else {
        this.bonusEventForm.get('frac')?.setValue([]);
        this.bonusEventForm.get('mine')?.setValue([]);
        this.selectedFracs = [];
        this.selectedFracsNumber = [];
        this.selectedMinesNumber = [];
      }
      if (!this.isDisableForm) {
        this.applyScopeChrome(mode);
      }
      this.checkDisablingContract(this.selectedRegions, this.selectedFracs);
      this.getBonusContracts();
      if (mode === 'fracs') {
        this.getBonusMines();
      }
      if (mode === 'regions') {
        this.getRegionsForBonus();
      }
    });

    this.bonusEventForm.get('frac')?.valueChanges.subscribe((selectedFracs) => {
      this.selectedFracs = this.constructIdArray(selectedFracs);
      this.selectedFracsNumber = this.selectedFracs.map((str) => parseInt(str, 10));
      if (this.bonusEventForm.get('scopeMode')?.value !== 'fracs') {
        return;
      }
      this.checkDisablingContract(this.selectedRegions, this.selectedFracs);
      this.getBonusContracts();
      this.getBonusMines();
      this.getRegionsForBonus();
    });

    this.bonusEventForm.get('region')?.valueChanges.subscribe((selectedRegions) => {
      this.selectedRegions = this.constructIdArray(selectedRegions);
      this.selectedRegionsNumber = this.selectedRegions.map((str) => parseInt(str, 10));
      if (this.bonusEventForm.get('scopeMode')?.value !== 'regions') {
        return;
      }
      this.checkDisablingContract(this.selectedRegions, this.selectedFracs);
      this.getBonusContracts();
    });

    this.bonusEventForm.controls['mine'].valueChanges.subscribe((selectedMines) => {
      this.selectedMinesNumber = this.constructIdArray(selectedMines).map((str) => parseInt(str, 10));
    });

    this.bonusEventForm.controls['contract'].valueChanges.subscribe((selectedContracts) => {
      this.selectedContractsNumber = this.constructIdArray(selectedContracts).map((str) => parseInt(str, 10));
    });

    this.bonusEventForm.get('type')?.valueChanges.subscribe((selectedType) => {
      this.syncPricingTypeSubjects(selectedType);
      this.applyPricingTypeValidators(selectedType);
    });
  }

  private _filterFrac(value) {
    const filterValue = value.toLowerCase();
    return this.fracs.filter((option) => option.jobName.toLowerCase().indexOf(filterValue) > -1);
  }

  private _filterMine(value) {
    const filterValue = value.toLowerCase();
    return this.mines.filter((option) => option.name.toLowerCase().indexOf(filterValue) > -1);
  }

  private _filterRegion(value) {
    const filterValue = value.toLowerCase();
    return this.regions.filter((option) => option.name.toLowerCase().indexOf(filterValue) > -1);
  }

  private _filterContract(value) {
    const filterValue = value.toLowerCase();
    return this.contracts.filter((option) => option.contractName.toLowerCase().indexOf(filterValue) > -1);
  }

  compareByValue(o1, o2) {
    return o1 && o2 && o1.id === o2.id;
  }

  getFracIdsAndJobNames() {
    this.bonusApiService.getFracIdsAndJobNames().subscribe((items) => {
      this.fracs = items;
      this.filteredFracs$ = this.filterFrac.valueChanges.pipe(
        startWith<string | any>(''),
        map((name) => (name ? this._filterFrac(name) : this.fracs)),
      );
    });
  }

  getBonusMines() {
    this.bonusApiService.getBonusMines(this.selectedFracs).subscribe((items) => {
      this.mines = items;
      this.filteredMines$ = this.filterMine.valueChanges.pipe(
        startWith<string | any>(''),
        map((name) => (name ? this._filterMine(name) : this.mines)),
      );
    });
  }

  getRegionsForBonus() {
    this.bonusApiService.getRegionsForBonus().subscribe((items) => {
      this.regions = items;
      this.filteredRegions$ = this.filterRegion.valueChanges.pipe(
        startWith<string | any>(''),
        map((name) => (name ? this._filterRegion(name) : this.regions)),
      );
    });
  }

  getBonusContracts() {
    const body = {
      regionIDs: this.selectedRegions,
      fracIDs: this.selectedFracs,
    };
    this.bonusApiService.getBonusContracts(body).subscribe((items) => {
      this.contracts = items;
      this.filteredContracts$ = this.filterContract.valueChanges.pipe(
        startWith<string | any>(''),
        map((name) => (name ? this._filterContract(name) : this.contracts)),
      );
    });
  }

  checkDisablingContract(regions, fracs) {
    if (regions.length === 0 && fracs.length === 0) {
      this.bonusEventForm.get('contract')?.disable();
    } else {
      this.bonusEventForm.get('contract')?.enable();
    }
  }

  constructIdArray(arrayObjects: any[]): string[] {
    const arrayOfNames = [];
    if (arrayObjects !== null && arrayObjects !== undefined) {
      for (const object of arrayObjects) {
        arrayOfNames.push(object.id.toString());
      }
    }
    return arrayOfNames;
  }

  removeChip(type: string, index: number) {
    const tempList = this.bonusEventForm.get(type)?.value;
    tempList.splice(index, 1);
    this.bonusEventForm.get(type)?.setValue(tempList);
  }
}
